<script>
import ButtonAll from '../UI/Button/ButtonAll.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'

export default {
  components: { ModalDialog, FormCreator, FieldCreator, ButtonAll },
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    rentId: { type: Number, required: true },
  },

  data: () => ({
    loading: false,
    loadForm: false,
    loadField: false,
    def: {},
    rent: {},
    fieldsOrder: {
      purchaseRate: 1,
      saleRate: 2,
      majoration: 3,
      decisionAmount: 4, // decisionAmount always in the second column of the second row
      monthly: 5,
      spacer: 6, // spacer if saleRate is hidden to keep profitability under decisionAmount
      profitability: 6,
    },
    formData: null,
  }),
  emits: ['rent-updated'],
  computed: {
    profitabilityClass() {
      const value = this.rent.profitability !== null ? this.rent.profitability : 0
      return value < 0 ? 'text-danger' : 'text-success'
    },
  },

  methods: {
    updateData(newData) {
      this.formData = newData
    },
    getFieldOrder(field) {
      // Adjust field order dynamically
      // For example, decisionAmount always stays in the second column of the second row
      const order = this.fieldsOrder[field]

      // Adjust the order if saleRate is hidden (shift everything forward)
      if (!this.hasRole('ROLE_SALE_RATE_SHOW') && field !== 'decisionAmount') {
        return order - 2
      }

      return order
    },
    closeModal() {
      this.loadForm = false
      this.loadField = false
      this.$refs[`modal-amount-calculator${this.rentId}`].close(false)
    },
    showForm() {
      this.loadForm = true
      this.$refs[`modal-amount-calculator${this.rentId}`].open()
    },
    globalBuild(data) {
      this.loading = data.loading
      if (data.build === true) {
        this.loadField = data.build
        this.addValue(this.def, data.def)
        this.addValue(this.rent, data.object)
      }
    },
    calculate() {
      this.$refs.formrent.methods()
    },
    objectReturn(data) {
      if (!data.sending) {
        this.$emit('rent-updated', data.object)
        this.setValue(this, 'rent', data.object)
        this.setValue(this.$refs.formrent, 'object', data.object)
      }
    },
  },
}
</script>
<template>
  <ModalDialog
    :id="'modal-amount-calculator' + rentId"
    :ref="'modal-amount-calculator' + rentId"
    id-modal-page="page-devis"
    class-modal-dialog="modal-lg mw-100 w-75"
    @icon-close="closeModal"
    e2e-modal="modal-amount-calculator"
  >
    <FormCreator
      @update-data="updateData"
      v-if="loadForm"
      ref="formrent"
      endpoint="rents"
      specific-endpoint="compute"
      method="patch"
      :id="rentId"
      :params="{ id: rentId, type: 'AMOUNT' }"
      class-to-add="d-none"
      @global-build="globalBuild"
      @object-return="objectReturn"
    />

    <template v-if="!loading && loadField" #body>
      <div class="flex-container" style="display: flex; flex-wrap: wrap; justify-content: space-between">
        <!-- First row -->
        <div
          class="flex-item"
          :style="{ order: getFieldOrder('purchaseRate') }"
          style="flex: 0 1 calc(50% - 10px); margin-bottom: 20px"
        >
          <FieldCreator :form-data="formData" name="purchaseRate" form-ref="formrent" domain="rent" label-top />
        </div>

        <div
          class="flex-item"
          v-if="hasRole('ROLE_SALE_RATE_SHOW')"
          :style="{ order: getFieldOrder('saleRate') }"
          style="flex: 0 1 calc(50% - 10px); margin-bottom: 20px"
        >
          <FieldCreator :form-data="formData" name="saleRate" form-ref="formrent" domain="rent" label-top />
        </div>

        <!-- Second row (decisionAmount always in the second column of the second row) -->
        <div
          class="flex-item"
          :style="{ order: getFieldOrder('majoration') }"
          style="flex: 0 1 calc(50% - 10px); margin-bottom: 20px"
        >
          <FieldCreator :form-data="formData" name="majoration" form-ref="formrent" domain="rent" label-top />
        </div>

        <div
          class="flex-item"
          :style="{ order: getFieldOrder('decisionAmount') }"
          style="flex: 0 1 calc(50% - 10px); margin-bottom: 20px"
        >
          <FieldCreator :form-data="formData" name="decisionAmount" form-ref="formrent" domain="rent" label-top />
        </div>

        <!-- Third row -->
        <div
          class="flex-item"
          :style="{ order: getFieldOrder('monthly') }"
          style="flex: 0 1 calc(50% - 10px); margin-bottom: 20px"
        >
          <FieldCreator :form-data="formData" name="monthly" form-ref="formrent" domain="rent" label-top />
        </div>

        <div
          v-if="!hasRole('ROLE_SALE_RATE_SHOW')"
          class="flex-item"
          :style="{ order: getFieldOrder('spacer') }"
          style="flex: 0 1 calc(50% - 10px); margin-bottom: 20px"
        ></div>

        <div
          class="flex-item"
          v-if="conf.theme_dir !== 'yneia'"
          :style="{ order: getFieldOrder('profitability') }"
          style="flex: 0 1 calc(50% - 10px); margin-bottom: 20px"
        >
          <div class="text-left">
            <div>{{ $t('demand.list.amountCession') }}</div>
            <div>{{ $n(Number(rent.amount), 'currency') }} {{ $t('main.dutyFree') }}</div>
          </div>
          <div :class="'my-2 text-left ' + profitabilityClass">
            <div><i class="fa fa-piggy-bank mr-1"></i> {{ $t('demand.fields.profitability.label') }}</div>
            <div>{{ $n(Number(rent.profitability), 'currency') }} {{ $t('main.dutyFree') }}</div>
          </div>
        </div>
      </div>
    </template>
    <template v-else #body>
      <div class="row justify-content-around mt-3">
        <div class="col-md-5 py-3 loading"></div>
        <div class="col-md-5 py-3 loading"></div>
      </div>
    </template>
    <template #footer>
      <div class="row w-100 justify-content-end">
        <div class="col-md-2">
          <ButtonAll margin="no" type="validation" label="button.calculate" @click="calculate" />
        </div>
        <div class="col-md-2">
          <ButtonAll margin="no" type="information" label="button.cancel" @click="closeModal" />
        </div>
      </div>
    </template>
  </ModalDialog>
</template>
